import { Hr } from '@euler/app/components/Hr';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { StatusColors } from '@euler/components/Colors';
import { EmptyView } from '@euler/components/EmptyView';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { FaultInspectionItem } from '@euler/model/FaultInspectionItem';
import { ReactNode, memo } from 'react';
import { StyleProp, TextStyle, ViewStyle } from 'react-native';
import { MaintenancePlanListItem } from '../types';
import { MaintenancePlanItemCell } from './MaintenancePlanItemCell';
import { Section } from './Section';

export const MaintenancePlanItemListPanel = memo(
  ({
    title,
    items,
    itemErrors,
    errorShakeNonce,
    titleRight,
    countLabelColor,
    emptyText,
    showEmptyIcon,
    emptyTextStyle,
    emptyViewStyle,
    style,
    onItemActionPress,
    onViewItemDetail,
    onEditItemPlanInfo,
    onEditItemNote,
    onViewFaultItemInspectionDetails,
    onViewFaultItemConstructionDetails,
  }: {
    title: string;
    items: MaintenancePlanListItem[];
    itemErrors?: Record<string, string>;
    errorShakeNonce?: number;
    countLabelColor?: string;
    emptyText: string | null;
    showEmptyIcon?: boolean;
    emptyTextStyle?: StyleProp<TextStyle>;
    emptyViewStyle?: StyleProp<ViewStyle>;
    style?: StyleProp<ViewStyle>;
    titleRight?: ReactNode;
    onItemActionPress: (item: MaintenancePlanListItem) => void;
    onViewItemDetail?: (item: MaintenancePlanListItem) => void;
    onEditItemPlanInfo?: (item: MaintenancePlanListItem) => void;
    onEditItemNote?: (item: MaintenancePlanListItem) => void;
    onViewFaultItemInspectionDetails?: (item: FaultInspectionItem) => void;
    onViewFaultItemConstructionDetails?: (item: FaultInspectionItem) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    if (!items.length && emptyText === null) {
      return null;
    }
    return (
      <Section
        title={title}
        titleRight={
          <StackLayout
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            style={{ flex: 1 }}
          >
            <Typography.Text color={countLabelColor} bold size={rpx(16)}>
              {`(${items.length})`}
            </Typography.Text>
            {titleRight}
          </StackLayout>
        }
        style={style}
      >
        {!items.length ? (
          <EmptyView
            showIcon={showEmptyIcon ?? true}
            iconSize={rpx(44)}
            iconColor={'#eee'}
            noFullHeight={true}
            message={emptyText}
            messageStyle={[
              {
                fontSize: rpx(14),
                fontFamily: FontFamily.NotoSans.Regular,
                marginTop: rpx(5),
              },
              emptyTextStyle,
            ]}
            buttonStyle={{
              backgroundColor: StatusColors.Info,
              height: rpx(36),
              borderTopLeftRadius: rpx(36),
              borderTopRightRadius: rpx(36),
              borderBottomLeftRadius: rpx(36),
              borderBottomRightRadius: rpx(36),
              minWidth: rpx(120),
              marginTop: rpx(15),
            }}
            buttonTextStyle={{
              color: '#fff',
            }}
            style={[
              {
                paddingBottom: rpx(15),
              },
              emptyViewStyle,
            ]}
          />
        ) : (
          <StackLayout gap={rpx(8)} divider={<Hr />}>
            {items.map(item => (
              <MaintenancePlanItemCell
                item={item}
                key={item.id}
                error={itemErrors?.[item.id]}
                errorShakeNonce={errorShakeNonce}
                onPressAction={onItemActionPress}
                onEditPlanInfo={onEditItemPlanInfo}
                onViewDetail={onViewItemDetail}
                onEditNote={onEditItemNote}
                onViewFaultRelatedInspectionDetails={
                  onViewFaultItemInspectionDetails
                }
                onViewFaultRelatedJobDetails={
                  onViewFaultItemConstructionDetails
                }
              />
            ))}
          </StackLayout>
        )}
      </Section>
    );
  },
);
